import React, { useEffect, useMemo, useState, useCallback } from "react";
import { formatConciergeRequestPayload } from "@companion-core/shared/app/Utils/wizardHelper";
import { DynamicFormJson } from "@companion-core/shared/app/Interfaces/wizard";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { BellHome } from "@companion-core/shared/app/Components/Bell/Home";
import { CategoryHeader } from "@companion-core/shared/app/Components/Bell/CategoryHeader";
import { BellConfirmation } from "@companion-core/shared/app/Components/Bell/Confirmation";
import { BellCategories } from "@companion-core/shared/app/Components/Bell/Categories/BellCategories";
import { DynamicForm } from "@companion-core/shared/app/Components/DynamicForm";
import { BellContentProps } from "./bell.model";
import { trackClickBell, trackStepBell } from "@companion-core/shared/app/tracking/bell";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { getInitialBellContent } from "@companion-core/shared/app/Utils/bell";
import { useWizardStore } from "@companion-core/shared/app/Store/wizardStore";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useNavigate } from "react-router";
import { useCreateRequest } from "@companion-core/shared/app/Queries/requests";
import { loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";
import { useMountEffect } from "@companion-core/shared/app/Hooks/useMountEffect";
import { useTranslation } from "react-i18next";
import "Assets/Styles/scss/bell.scss";

interface ContentComponentProps {
  isBellHomeEnabled: boolean;
  bellContent: string;
  jsonForm: DynamicFormJson | null;
  dynamicFormName: string;
  onSubmitForm: (payload: Record<string, unknown>, refetch?: boolean) => Promise<void>;
  status: "error" | "idle" | "pending" | "success" | undefined;
  onConfirmationButtonClicked: () => Promise<void>;
  fromOnboarding: boolean;
  onClose: () => void;
}

const OnboardingRedirect = ({ onClose }: { onClose: () => void }) => {
  useMountEffect(() => {
    onClose();
  });

  return <Navigate to="/request-confirmation" />;
};

const ContentComponent = ({
  isBellHomeEnabled,
  bellContent,
  jsonForm,
  dynamicFormName,
  onSubmitForm,
  status,
  onConfirmationButtonClicked,
  fromOnboarding,
  onClose,
}: ContentComponentProps) => {
  const { t } = useTranslation();
  if (isBellHomeEnabled && bellContent === "home") {
    return <BellHome />;
  }
  if (bellContent === "categories") {
    return <BellCategories />;
  }
  if (bellContent === "form" && jsonForm) {
    return (
      <DynamicForm
        titleTag="h3"
        name={dynamicFormName}
        initialFormJSON={jsonForm}
        onSubmitForm={onSubmitForm}
        submitButtonAccessibilityLabel={t("request.concierge.form.submit.accessibilityLabel")}
      />
    );
  }
  if (bellContent === "confirmation") {
    if (fromOnboarding && status === "success") {
      return <OnboardingRedirect onClose={onClose} />;
    } else {
      return <BellConfirmation requestStatus={status} onClick={onConfirmationButtonClicked} />;
    }
  }
};

export const BellContent = ({ onClose }: BellContentProps) => {
  const isBellHomeEnabled: boolean = getModule("concierge")?.modules.bell.options.home.enabled;
  const {
    bellContent,
    bellCategory,
    formPath,
    payload,
    vendorApiValues,
    bellReferrer,
    setPayload,
    setBellContent,
    clearBell,
    fromOnboarding,
  } = useBellStore();
  const { resetWizard } = useWizardStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [jsonForm, setJsonForm] = useState<DynamicFormJson | null>(null);
  const { mutateAsync: sendBellRequest, reset, status } = useCreateRequest();

  useEffect(() => {
    setJsonForm(null);
    resetWizard();
    if (formPath) {
      setJsonForm(loadFormFromPath(formPath));
    }
  }, [formPath]);

  const dynamicFormName = useMemo(() => {
    const splitPath = formPath?.split("/");
    if (!splitPath) return "form";
    return splitPath[splitPath.length - 1].replace(".json", "");
  }, [formPath]);

  const onSubmitForm = async (payload: Record<string, unknown>, refetch = false) => {
    if (!refetch) {
      setPayload(payload);
    }
    const category: string = payload?.family
      ? (payload.family as string)
      : vendorApiValues?.family
        ? (vendorApiValues?.family as string)
        : (bellCategory?.category ?? "");
    trackClickBell(!refetch ? "bell_submit" : "retry_submit", category, "", "", bellReferrer);
    const formattedPayload: Record<string, unknown> = formatConciergeRequestPayload(
      {
        ...vendorApiValues,
        ...payload,
      },
      category
    );
    try {
      await sendBellRequest({
        payload: formattedPayload,
      });
    } finally {
      trackStepBell(status, "bell", category);
      setBellContent("confirmation");
    }
  };

  /**
   * This function is used to reset the wizard and the bell and go to the "home" page
   */
  const resetAll = useCallback(() => {
    onClose();
    resetWizard();
    clearBell(getInitialBellContent());
  }, [resetWizard, clearBell]);

  const onConfirmationButtonClicked = useCallback(async () => {
    if (status === "success") {
      trackClickBell("My_Request", "", "My_Request");
      queryClient.invalidateQueries({ queryKey: ["my-requests"] });
      resetAll();
      navigate("/my-requests", { state: { canGoBack: true } });
    } else if (status === "error") {
      reset(); // reset mutation to retry into error page
      await onSubmitForm(payload, true);
    }
  }, [status]);

  return (
    <div data-testid="bell__content" className="d-flex bell__content">
      {bellCategory && bellContent !== "confirmation" && <CategoryHeader category={bellCategory} />}
      <ContentComponent
        bellContent={bellContent ?? ""}
        dynamicFormName={dynamicFormName}
        isBellHomeEnabled={isBellHomeEnabled}
        jsonForm={jsonForm}
        onConfirmationButtonClicked={onConfirmationButtonClicked}
        onSubmitForm={onSubmitForm}
        status={status}
        onClose={resetAll}
        fromOnboarding={fromOnboarding}
      />
    </div>
  );
};
